import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, RefreshCw, AlertTriangle, Copy, Check } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUGGESTIONS = [
  "Satın almak için en güçlü 3 adayı seç",
  "Kira getirisi en iyi olanları karşılaştır",
  "En riskli ilanları göster",
  "Pazarlık için hedef fiyat öner",
  "Eksik bilgileri listele",
];

export default function PortfolioAnalysis() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);        // fresh POST running
  const [initialLoading, setInitialLoading] = useState(true); // initial GET cache
  const [error, setError] = useState(null);
  const [question, setQuestion] = useState("");
  const [analysis, setAnalysis] = useState(null);       // full response object
  const [copied, setCopied] = useState(false);

  const fetchCache = useCallback(async () => {
    setInitialLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API_URL}/api/ai/portfolio-analyze`, { withCredentials: true });
      setAnalysis(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Yükleme hatası");
    } finally {
      setInitialLoading(false);
    }
  }, []);

  useEffect(() => { fetchCache(); }, [fetchCache]);

  const runAnalysis = useCallback(async (force = true) => {
    setLoading(true);
    setError(null);
    try {
      // Step 1: POST — either returns a cached hit immediately, or 202 with a job_id.
      const startRes = await axios.post(
        `${API_URL}/api/ai/portfolio-analyze`,
        { question: question.trim() || null, force },
        { withCredentials: true, timeout: 30_000 }
      );
      const data = startRes.data;

      if (data.empty) {
        setAnalysis(null);
        toast.info(data.message || "Portföy boş");
        return;
      }
      if (data.result) {
        // Cache hit — completed inline.
        setAnalysis(data);
        toast.success(data.cached ? "Önbellekten yüklendi" : "Analiz oluşturuldu");
        return;
      }
      if (!data.job_id) {
        throw new Error("Beklenmeyen yanıt");
      }

      // Step 2: poll the job until completed or failed. Cap total wait at ~5 min.
      const jobId = data.job_id;
      const startedAt = Date.now();
      const MAX_WAIT_MS = 5 * 60_000;
      const POLL_INTERVAL_MS = 3000;
      while (Date.now() - startedAt < MAX_WAIT_MS) {
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
        const poll = await axios.get(
          `${API_URL}/api/ai/portfolio-analyze/status/${jobId}`,
          { withCredentials: true, timeout: 15_000 }
        );
        const pd = poll.data;
        if (pd.status === "completed") {
          setAnalysis({
            result: pd.result,
            generated_at: pd.generated_at,
            data_hash: pd.data_hash,
            current_data_hash: pd.data_hash,
            question: pd.question,
            properties_analyzed: pd.properties_analyzed,
            cached: false,
          });
          toast.success("Yeni analiz oluşturuldu");
          return;
        }
        if (pd.status === "failed") {
          const err = pd.error?.message || "Analiz başarısız oldu. Lütfen tekrar deneyin.";
          throw new Error(err);
        }
        // status is 'queued' or 'running' — keep polling.
      }
      throw new Error("Analiz zaman aşımına uğradı. Lütfen tekrar deneyin.");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.response?.data?.error?.message || e.message || "Analiz hatası";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [question]);

  const copyResult = useCallback(async () => {
    if (!analysis?.result) return;
    try { await navigator.clipboard.writeText(analysis.result); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error("Kopyalanamadı"); }
  }, [analysis]);

  const hasResult = !!analysis?.result;
  const stale = !!analysis?.stale;
  const empty = analysis && analysis.empty;
  const propertiesCount = analysis?.properties_count ?? analysis?.properties_analyzed;

  return (
    <div data-testid="portfolio-analysis-page" className="min-h-screen bg-[#F9FAFB] dark:bg-[#09090B]">
      <header className="sticky top-0 z-10 border-b border-[#E4E4E7] dark:border-[#27272A] bg-white/90 dark:bg-[#18181B]/90 backdrop-blur">
        <div className="max-w-4xl mx-auto flex items-center gap-3 px-4 py-3">
          <Button variant="outline" size="sm" onClick={() => navigate(-1)} data-testid="portfolio-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Geri
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-[#0A0A0A] dark:text-[#FAFAFA] flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#F59E0B]" /> Portföy AI Analizi
            </h1>
            <p className="text-[11px] text-[#52525B] dark:text-[#A1A1AA] max-w-2xl">
              Kayıtlı gayrimenkullerinizi fiyat, konum, kira potansiyeli, toplam yatırım maliyeti, teknik durum, görüşme notları ve veri yeterliliğine göre karşılaştırır.
            </p>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-5 space-y-4">
        {/* Optional question */}
        <div className="bg-white dark:bg-[#18181B] border border-[#E4E4E7] dark:border-[#27272A] rounded-lg p-4">
          <label className="text-xs font-semibold text-[#52525B] dark:text-[#A1A1AA]">Özel sorunuzu yazın (isteğe bağlı)</label>
          <Textarea
            data-testid="portfolio-question"
            value={question}
            onChange={(e) => setQuestion(e.target.value.slice(0, 500))}
            rows={2}
            placeholder="Örn: Kira getirisi en iyi 2 ilanı karşılaştır"
            className="mt-2 bg-white dark:bg-[#09090B] border-[#E4E4E7] dark:border-[#27272A]"
          />
          <div className="mt-2 flex flex-wrap gap-2">
            {SUGGESTIONS.map((s, i) => (
              <button
                key={i}
                data-testid={`suggestion-${i}`}
                onClick={() => setQuestion(s)}
                className="text-[11px] px-2.5 py-1 rounded-full border border-[#E4E4E7] dark:border-[#27272A] text-[#52525B] dark:text-[#A1A1AA] hover:bg-[#F9FAFB] dark:hover:bg-[#27272A]"
              >
                {s}
              </button>
            ))}
          </div>
          <div className="mt-3 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
            <div className="text-[11px] text-[#52525B] dark:text-[#A1A1AA]">
              {propertiesCount != null
                ? `${propertiesCount} kayıt analiz edilecek`
                : "Portföyünüzün tamamı sunucudan çekilecek"}
            </div>
            <Button
              data-testid="run-portfolio-analysis"
              onClick={() => runAnalysis(true)}
              disabled={loading}
              className="bg-[#F59E0B] hover:bg-[#D97706] text-white w-full sm:w-auto"
            >
              {loading
                ? (<><RefreshCw className="w-4 h-4 mr-2 animate-spin"/>Portföy analiz ediliyor…</>)
                : (hasResult ? "Yeniden Analiz Et" : "Tüm Portföyü Analiz Et")
              }
            </Button>
          </div>
        </div>

        {/* Meta / states */}
        {initialLoading && (
          <div data-testid="portfolio-initial-loading" className="text-center text-sm text-[#52525B] dark:text-[#A1A1AA] py-6">Veriler hazırlanıyor…</div>
        )}
        {empty && !loading && (
          <div data-testid="portfolio-empty" className="text-center text-sm text-[#52525B] dark:text-[#A1A1AA] py-6 border border-dashed border-[#E4E4E7] dark:border-[#27272A] rounded">
            Portföyünüzde henüz kayıt yok. Analiz için önce en az bir gayrimenkul ekleyin.
          </div>
        )}
        {error && (
          <div data-testid="portfolio-error" className="border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded p-3 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0"/>
            <div className="flex-1 text-xs">{error}</div>
            <Button size="sm" variant="outline" onClick={() => runAnalysis(true)} data-testid="portfolio-retry">Tekrar Dene</Button>
          </div>
        )}

        {hasResult && (
          <div data-testid="portfolio-result-card" className="bg-white dark:bg-[#18181B] border border-[#E4E4E7] dark:border-[#27272A] rounded-lg overflow-hidden">
            <div className="border-b border-[#E4E4E7] dark:border-[#27272A] px-4 py-2 flex items-center justify-between text-[11px]">
              <div className="text-[#52525B] dark:text-[#A1A1AA]">
                Son analiz: <span className="font-mono text-[#0A0A0A] dark:text-[#FAFAFA]">{new Date(analysis.generated_at).toLocaleString("tr-TR")}</span>
                {analysis.properties_analyzed != null && (
                  <span className="ml-2">· {analysis.properties_analyzed} kayıt</span>
                )}
              </div>
              <button
                data-testid="portfolio-copy"
                onClick={copyResult}
                className="text-[11px] px-2 py-1 rounded border border-[#E4E4E7] dark:border-[#27272A] flex items-center gap-1 hover:bg-[#F9FAFB] dark:hover:bg-[#27272A]"
              >
                {copied ? <><Check className="w-3 h-3"/> Kopyalandı</> : <><Copy className="w-3 h-3"/> Kopyala</>}
              </button>
            </div>
            {stale && (
              <div data-testid="portfolio-stale" className="bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 text-[11px] px-4 py-2 border-b border-amber-200 dark:border-amber-800 flex items-start gap-2">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span data-testid="portfolio-stale-summary">
                  {analysis?.stale_summary ||
                    "Portföy verileri değişti — yeniden analiz önerilir."}
                </span>
              </div>
            )}
            <div
              data-testid="portfolio-result"
              className="prose prose-sm dark:prose-invert max-w-none px-4 py-4 max-h-[70vh] overflow-y-auto text-[#0A0A0A] dark:text-[#FAFAFA] prose-headings:text-[#0A0A0A] dark:prose-headings:text-[#FAFAFA] prose-strong:text-[#0A0A0A] dark:prose-strong:text-[#FAFAFA] prose-table:text-[13px] prose-th:border prose-th:border-[#E4E4E7] dark:prose-th:border-[#27272A] prose-td:border prose-td:border-[#E4E4E7] dark:prose-td:border-[#27272A]"
            >
              <div className="overflow-x-auto">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  // ReactMarkdown v9 does not render raw HTML by default,
                  // so untrusted <script>/<img onerror>/... inside the AI
                  // output cannot execute. Only Markdown syntax is honoured.
                  components={{
                    a: ({ node, ...props }) => (
                      <a {...props} target="_blank" rel="noopener noreferrer nofollow" />
                    ),
                    table: ({ node, ...props }) => (
                      <table {...props} className="text-left" />
                    ),
                  }}
                >
                  {analysis.result || ""}
                </ReactMarkdown>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
